import askAboutObjectsApi from 'common/src/modules/askAboutObjects/askAboutObjects.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import {SearchObjectType} from 'common/src/modules/askAboutObjects/askAboutObjects.enums';

const querySchema = z.object({
  objectType: z
    .nativeEnum(SearchObjectType)
    .describe(
      'Placement and Signal queries are scoped to the current campaign version. ' +
        'Dsp questions are about DSP certification, macros, creative specs and data settings. ' +
        'Signal queries support browsing signal categories, searching signals by name, returning signal details (name, key, type, supported operators, categories), and retrieving feed values or predefined optional values.',
    ),
  question: z
    .string()
    .describe(
      'Natural language question about the objects. ' +
        'For Signal queries, always state the purpose: condition card, product matching strategy, or content matching strategy.',
    ),
});

const askAboutObjectsToolSchema = z.object({
  queries: z
    .array(querySchema)
    .min(1)
    .describe('List of object type and question pairs to ask about'),
});

export default function askAboutObjectsTool(store: CLStore) {
  async function execute({
    input: {queries},
  }: ToolExecuteParams<typeof askAboutObjectsToolSchema>): Promise<ToolResult> {
    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    const context = {
      campaignId: campaign?.id ?? null,
      campaignVersionId: versionId ?? null,
    };

    const responses = await Promise.all(
      queries.map(({objectType, question}) =>
        askAboutObjectsApi.askAboutObjects({objectType, question, context}),
      ),
    );

    const formattedResults = queries
      .map(({objectType, question}, index) => {
        const response = responses[index];

        return `[${objectType}] "${question}"\nAnswer: ${response.answer}`;
      })
      .join('\n\n---\n\n');

    return toolResult(formattedResults);
  }

  return tool({
    name: CampaignSetupAgentToolName.AskAboutObjects,
    displayName: 'Asking about objects',
    description:
      'Search account libraries for relevant objects or specific details. ' +
      'Object hierarchy: Libraries contain Feeds, Creatives, DSPs, and Campaigns. ' +
      'Feeds include Location Feeds, Product Feeds, and Content Feeds. ' +
      'Product Feeds contain Product Items, Product Sets (list of items), Top Items (list of items), and Category Top Items (list of categories, each with a list of items). ' +
      'Content Feeds contain Content Items and Content Sets (list of items). ' +
      'Location Feeds contain Location Items and Location Sets (list of items). ' +
      'Campaigns contain Placements, each belonging to a specific DSP. ' +
      'Signals are campaign-scoped runtime parameters used for conditioning. Signal queries support browsing categories, searching by name, and returning signal details and values. ' +
      'For feeds, supports retrieval of used/mapped taxonomy fields/columns and querying feed items. ' +
      'When querying by exact IDs, include all IDs in the question. Otherwise, describe the search criteria or the specific information needed concisely. ' +
      'IDs in answers follow expected format: <prefix><object_number>. Prefix meanings: ' +
      `${IdPrefix.ProductFeed} product feed, ${IdPrefix.LocationFeed} location feed, ${IdPrefix.ContentFeed} content feed, ` +
      `${IdPrefix.ProductSet} product set, ${IdPrefix.LocationSet} location set, ${IdPrefix.ContentSet} content set, ` +
      `${IdPrefix.TopItems} top items, ${IdPrefix.CategoryTopItems} category top items, ` +
      `${IdPrefix.Creative} creative, ${IdPrefix.Dsp} DSP. ` +
      'Use that prefixed format when referencing those objects in tool calls.',
    execute,
    inputSchema: askAboutObjectsToolSchema,
    isReadonly: true,
  });
}
